import React from "react";
import { pick } from "@/lib/languages";
import { Link } from "react-router-dom";
import { Star, Bed, CheckCircle } from "@phosphor-icons/react";
import { useSettings } from "@/context/SettingsContext";
import { mediaUrl } from "@/lib/api";
import { ratingWord } from "@/lib/ratings";
import { useFormat } from "@/lib/format";
import { hotelCardL as L, hotelsL } from "@/i18n/stays";
import { staysUiL } from "@/i18n/staypages";
import { dsL } from "@/i18n/ds";
import { cancellationTermsL } from "@/i18n/cancellation";
import ResultCard from "@/components/ds/ResultCard";
import Price from "@/components/ds/Price";
import { Button } from "@/components/ui/button";

// How wide a result's photo is drawn: the card's width on a phone, a 160 px
// column from 640 px, 176 px from 1024 px beside the map and 196 px from 1536 px.
const ROW_SIZES = "(min-width: 1536px) 196px, (min-width: 1024px) 176px, (min-width: 640px) 160px, 100vw";

// The classification in gold, small, the way a traveller reads stars beside a
// name; the screen reader hears it as one phrase.
export function Stars({ count, l, size = 11 }) {
  if (!(count > 0)) return null;
  return (
    <span role="img" aria-label={l.stars(count)} className="inline-flex shrink-0 items-center gap-0.5 text-touki-star">
      {Array.from({ length: count }).map((_, i) => <Star key={i} size={size} weight="fill" aria-hidden="true" />)}
    </span>
  );
}

// The guest-rating word (lib/ratings), kept importable from here for the property page.
export { ratingWord };

/** "4,6/5 · Très bien · 214 avis vérifiés" from real reviews only; otherwise "Pas encore d'avis". */
function Rating({ hotel, lang }) {
  const f = useFormat();
  const ui = pick(staysUiL, lang).card;
  const rated = hotel.rating > 0 && hotel.review_count > 0;
  if (!rated) return <p className="text-body-sm text-touki-muted" data-testid="stay-no-reviews">{ui.noReviews}</p>;
  const score = f.number(hotel.rating, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const word = ratingWord(hotel.rating, pick(hotelsL, lang).scores);
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm" data-testid="stay-rating">
      <span className="inline-flex h-6 items-center rounded-sm bg-touki-dark px-2 font-bold text-white tabular-nums" dir="ltr">{score}{ui.outOf}</span>
      {word ? <span className="font-semibold text-touki-black">{word}</span> : null}
      <span className="text-touki-muted">{ui.reviews(hotel.review_count)}</span>
    </p>
  );
}

/** The cancellation line beside the price, from the deadline the page computed (the apps' card draws it too). */
export function Terms({ terms, lang, iconWeight = "bold" }) {
  const f = useFormat();
  const t = pick(staysUiL, lang).terms;
  if (!terms) return null;
  if (terms.kind === "until") {
    return (
      <p className="flex items-start gap-1.5 font-semibold text-touki-success" data-testid="stay-terms" data-kind="until">
        <CheckCircle size={16} weight={iconWeight} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>{t.freeUntil(`${f.dateTime(terms.at, { timeZone: "UTC" })} UTC`)}</span>
      </p>
    );
  }
  if (terms.kind === "nonRefundable") {
    return <p className="text-touki-muted" data-testid="stay-terms" data-kind="nonRefundable">{pick(cancellationTermsL, lang).policy.non_refundable}</p>;
  }
  return <p className="text-touki-muted" data-testid="stay-terms" data-kind="none">{t.closed}</p>;
}

/**
 * A stay as a search result (Results.dc.html). Home's featured stays have
 * their own card (components/home/FeaturedStays); the old `horizontal` flag
 * a caller may still pass changes nothing.
 *
 * The result reads, in order: the real photo, the name, its stars with the
 * kind and the place on one line under it (the same place whatever the
 * name's length), the real rating (or "no reviews yet"), the cancellation
 * deadline; then the price column: the total for the dates and the party
 * first, the rate per night under it, and one outline action. No red on a
 * card: the red is the property page's Reserve.
 *
 * `href` carries the search to the property page (dates, guests); `offer`
 * is what the page computed for this search: { total, nightly, nights,
 * rooms, guests, terms } (pages/hotels/stayQuote).
 */
export default function HotelCard({ hotel, headingLevel = 3, priority = false, href, offer, kind }) {
  // The card sits under the h1 of a results page (h2) or a section (h3): the
  // page decides the level and the outline stays in order.
  const Heading = `h${headingLevel}`;
  const { lang } = useSettings();
  const l = pick(L, lang);
  const image = hotel.images?.[0];
  const location = [...new Set([hotel.neighborhood, hotel.city, hotel.region].filter(Boolean))].join(", ");
  // `kind` is the page's grouping of the stay (hotel, apartment, house), for a
  // catalogue row that names only its property_type.
  const typeLabel = l.types[kind || hotel.stay_type];
  const to = href || `/hotels/${hotel.id}`;
  return <ResultRow hotel={hotel} Heading={Heading} priority={priority} to={to} offer={offer} lang={lang} l={l} location={location} typeLabel={typeLabel} image={image} kind={kind || hotel.stay_type} />;
}

function ResultRow({ hotel, Heading, priority, to, offer, lang, l, location, typeLabel, image, kind }) {
  const ui = pick(staysUiL, lang);
  const count = pick(dsL, lang).count;
  const home = ["apartment", "house"].includes(kind);
  const cta = home ? ui.card.seeHome : ui.card.seeRooms;
  const nightly = offer?.nightly ?? hotel.price_from;
  const total = offer?.total ?? null;
  // What the total covers, as the traveller asked: "3 nuits, 2 voyageurs";
  // a party that fills more than one room says so.
  const context = total != null
    ? [count.nights(offer.nights), offer.rooms > 1 ? ui.card.rooms(offer.rooms) : null, offer.guests ? count.travellers(offer.guests) : null].filter(Boolean).join(", ")
    : ui.card.perNightOnly;
  const unit = total != null && offer.nights > 0 ? { amount: Math.round(total / offer.nights), per: "night" } : null;
  return (
    <ResultCard data-testid={`hotel-card-${hotel.id}`} data-stay-id={hotel.id}>
      <ResultCard.Media src={image ? mediaUrl(image) : null} icon={Bed} priority={priority} sizes={ROW_SIZES} className="lg:h-[148px] lg:w-[176px] 2xl:h-[152px] 2xl:w-[196px]" />
      <ResultCard.Body>
        <ResultCard.Title as={Heading} className="min-w-0">
          <ResultCard.Link as={Link} to={to}>{hotel.name}</ResultCard.Link>
        </ResultCard.Title>
        {/* The stars open the line under the name, so they sit in the same place on every card. */}
        {(hotel.stars > 0 || typeLabel || location) && (
          <ResultCard.Meta className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <Stars count={hotel.stars} l={l} size={12} />
            {typeLabel || location ? <span>{[typeLabel, location].filter(Boolean).join(" · ")}</span> : null}
          </ResultCard.Meta>
        )}
        <Rating hotel={hotel} lang={lang} />
        {offer?.terms ? <ResultCard.Terms><Terms terms={offer.terms} lang={lang} /></ResultCard.Terms> : null}
      </ResultCard.Body>
      <ResultCard.Aside className="sm:w-44 lg:w-48">
        {nightly != null
          ? <Price amount={total ?? nightly} context={context} unit={unit} align="responsive" data-testid="stay-price" />
          : <span />}
        {/* It opens the same page as the name: one link for the keyboard and a screen reader. */}
        <ResultCard.Action duplicate>
          <Button asChild variant="outline" className="px-4 text-button-md">
            <Link to={to} aria-label={ui.card.seeLabel(cta, hotel.name)} data-testid={`hotel-cta-${hotel.id}`}>{cta}</Link>
          </Button>
        </ResultCard.Action>
      </ResultCard.Aside>
    </ResultCard>
  );
}
